import { EntityResponse } from '../api/entityResponse'
import { authedRequest } from '../api/requests'

const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

class ProcessBlocker {
  blocked = false
  resolver: ((value: unknown) => void) | null = null
  async wait() {
    return new Promise((resolve) => {
      this.resolver = resolve
    })
  }
  free() {
    if (this.resolver) {
      this.resolver(null)
      this.resolver = null
    }
    this.blocked = false
  }
}

const adminBlocker = new ProcessBlocker()

class PermissionService {
  private cache = new Map<string, { value: boolean; expiry: number }>()

  async isAdmin(): Promise<boolean> {
    // prevent multiple simultaneous admin checks
    if (this.getCached('Admin') === null) {
      if (adminBlocker.blocked) {
        await adminBlocker.wait()
      } else {
        adminBlocker.blocked = true
      }
    }
    const [result] = await this.checkPermissions(['Admin'])
    adminBlocker.free()
    return result || false
  }

  async checkPermissions(resourceCodes: string[]): Promise<boolean[]> {
    if (!resourceCodes?.length) return []

    // Check if user is Admin
    if (this.getCached('Admin') === true) {
      return resourceCodes.map(() => true)
    }

    // Separate cached and uncached codes
    const uncached: string[] = []
    const results = new Map<string, boolean>()

    for (const code of resourceCodes) {
      const cached = this.getCached(code)
      if (cached !== null) {
        results.set(code, cached)
      } else {
        uncached.push(code)
      }
    }

    // Return cached results if all are cached
    if (!uncached.length) {
      return resourceCodes.map((code) => results.get(code) || false)
    }

    // Fetch uncached permissions from backend
    try {
      const response = (await authedRequest('BaseBiz', '/AccessResource/Match', 'POST', {
        stringList: uncached,
      })) as EntityResponse

      if (response.isSuccess && Array.isArray(response.entity)) {
        uncached.forEach((code, index) => {
          const hasPermission = response.entity[index] || false
          this.setCached(code, hasPermission)
          results.set(code, hasPermission)
        })
      } else {
        uncached.forEach((code) => results.set(code, false))
      }
    } catch (error) {
      console.error('Failed to check permissions:', error)
      uncached.forEach((code) => results.set(code, false))
    }

    return resourceCodes.map((code) => results.get(code) || false)
  }

  async hasPermission(resourceCode: string): Promise<boolean> {
    const [result] = await this.checkPermissions([resourceCode])
    return result || false
  }

  async hasAnyPermission(resourceCodes: string[]): Promise<boolean> {
    const permissions = await this.checkPermissions(resourceCodes)
    return permissions.some((p) => p)
  }

  async hasAllPermissions(resourceCodes: string[]): Promise<boolean> {
    const permissions = await this.checkPermissions(resourceCodes)
    return permissions.every((p) => p)
  }

  private getCached(code: string): boolean | null {
    const item = this.cache.get(code)
    if (!item) return null
    if (Date.now() > item.expiry) {
      this.cache.delete(code)
      return null
    }
    return item.value
  }

  private setCached(code: string, value: boolean): void {
    this.cache.set(code, { value, expiry: Date.now() + CACHE_DURATION })
  }

  clearCache(): void {
    this.cache.clear()
  }

  clearCacheForResource(code: string): void {
    this.cache.delete(code)
  }

  getCacheSize(): number {
    return this.cache.size
  }
}

export const permissionService = new PermissionService()
